// @flow
import React, { useEffect, useCallback, useRef } from 'react';
import Debug from 'debug';
import { FormattedMessage } from 'react-intl';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import type { DebugConfig, TISMessage, CarEntity, PlaneEntity, Car, Plane } from '../../types';
import { TYPE, DATA_SOURCE_BUFFER_SIZE } from '../../constants/targetConstants';
import targetModel from '../../models/target';
import tis from '../../services/tis';
import SyncCarContainer from './containers/SyncCarContainer';
import SyncPlaneContainer from './containers/SyncPlaneContainer';

const debug = Debug('amms:modules:syncTargets');

let hidden, visibilityChange;
if(typeof document !== 'undefined') {
    if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
        hidden = "hidden";
        visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
        hidden = "msHidden";
        visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
        hidden = "webkitHidden";
        visibilityChange = "webkitvisibilitychange";
    }
}

const filter = (msg: TISMessage, debugConfig: DebugConfig) => {
    if(debugConfig) {
        const icao = msg.Icao && msg.Icao.toLowerCase();
        if(debugConfig.tisFilter && icao !== debugConfig.tisFilter.toLowerCase()) {
            return false;
        }
    }

    if(!msg.Icao || !msg.MovementData || !msg.MovementData.Position) {
        return false;
    }
    if( isNaN(parseFloat(msg.MovementData.Position[0])) || isNaN(parseFloat(msg.MovementData.Position[1])) ) {
        return false;
    }

    return true;
};

export type Props = $ReadOnly<{
    vehicles: $ReadOnlyArray<string>,
    carEntities: $ReadOnly<{
        [icao: string]: CarEntity
    }>,
    planeEntities: $ReadOnly<{
        [icao: string]: PlaneEntity
    }>,
    debugConfig: DebugConfig,
    tisConfig: $ReadOnly<{
        enabled: boolean,
        throttling: boolean,
        interval: number
    }>,

    setTisEnabled: (enabled: boolean) => void,
    setCar: (car: Car) => void,
    setCars: (cars: $ReadOnly<{[icao: string]: $ReadOnlyArray<Car>}>) => void,
    setPlane: (plane: Plane) => void,
    setPlanes: (planes: $ReadOnly<{[icao: string]: $ReadOnlyArray<Plane>}>) => void,
}>;

function SyncTargets({ vehicles, carEntities, planeEntities, debugConfig, tisConfig, setTisEnabled, setCar, setCars, setPlane, setPlanes }: Props) {
    const vehiclesRef = useRef(vehicles);
    const messageQueue = useRef({});

    const handleVisibilityChange = useCallback(() => {
        if (document[hidden]) {
            debug('Window hidden, disabling TIS');
            setTisEnabled(false);
        } else {
            debug('Window visible, enabling TIS');
            setTisEnabled(true);
        }
    }, [setTisEnabled]);

    useEffect(() => {
        if(typeof hidden !== 'undefined') {
            document.addEventListener(visibilityChange, handleVisibilityChange, false);
        }

        return () => {
            if(typeof hidden !== 'undefined') {
                document.removeEventListener(visibilityChange, handleVisibilityChange);
            }
        };
    }, [handleVisibilityChange]);

    useEffect(() => {
        vehiclesRef.current = vehicles;
    }, [vehicles]);

    useEffect(() => {
        let timerId;

        debug(`TIS ${tisConfig.enabled ? 'enabled' : 'disabled'}`);
        if(tisConfig.enabled) {
            debug(`TIS throttling ${tisConfig.throttling ? 'enabled' : 'disabled'} with interval ${tisConfig.interval}`);
        }

        if(tisConfig.enabled && tisConfig.throttling) {
            const processQueue = () => {
                const cars = {};
                const planes = {};
                for(const icao in messageQueue.current) {
                    if(Object.prototype.hasOwnProperty.call(messageQueue.current, icao)) {
                        const messages = messageQueue.current[icao];

                        for(let i = 0, length = messages.length; i < length; i += 1) {
                            const target = targetModel(messages[i], vehiclesRef.current);

                            let map;
                            if(target.type === TYPE.car) {
                                map = cars;
                            }
                            else if(target.type === TYPE.plane) {
                                map = planes;
                            }

                            if(!map[icao]) {
                                map[icao] = [];
                            }

                            map[icao].unshift(target);
                        }
                    }
                }

                messageQueue.current = {};

                if(Object.keys(cars).length > 0) {
                    setCars(cars);
                }
                if(Object.keys(planes).length > 0) {
                    setPlanes(planes);
                }
            };

            processQueue();
            timerId = setInterval(processQueue, tisConfig.interval);
        }

        return () => {
            clearInterval(timerId);
        };
    }, [tisConfig, setCars, setPlanes]);

    const onTargetUpdate = useCallback((msg: TISMessage) => {
        if(debugConfig.tisLogger) {
            if(!debugConfig.tisLoggerFilter || (msg.Icao && msg.Icao.toLowerCase().indexOf(debugConfig.tisLoggerFilter.toLowerCase()) !== -1) ) {
                console.log('TIS Message: ', msg);
            }
        }

        if(filter(msg, debugConfig)) {
            msg = {
                ...msg,
                Icao: msg.Icao && msg.Icao.toLowerCase()
            };

            if(tisConfig.throttling) {
                if(!messageQueue.current[msg.Icao]) {
                    messageQueue.current[msg.Icao] = [];
                }

                messageQueue.current[msg.Icao].unshift(msg);

                if(messageQueue.current[msg.Icao].length > DATA_SOURCE_BUFFER_SIZE) {
                    messageQueue.current[msg.Icao].splice(DATA_SOURCE_BUFFER_SIZE);
                }
            }
            else {
                const target = targetModel(msg, vehiclesRef.current);

                if(target.type === TYPE.car) {
                    setCar(target);
                }
                else if(target.type === TYPE.plane) {
                    setPlane(target);
                }
            }
        }
    }, [tisConfig.throttling, debugConfig, setCar, setPlane]);

    useEffect(() => {
        if(tisConfig.enabled) {
            tis.on('targetUpdate', onTargetUpdate);
            tis.enable();
        }
        else {
            tis.disable();
        }

        return () => {
            tis.removeListener('targetUpdate', onTargetUpdate);
        };
    }, [tisConfig.enabled, onTargetUpdate]);

    return (
        <React.Fragment>
            {!tisConfig.enabled &&
                <Dialog open={true}>
                    <DialogTitle id="simple-dialog-title">
                        <FormattedMessage
                            id="tisService.disabled"
                            defaultMessage="TIS Service is disabled"
                        />
                    </DialogTitle>
                </Dialog>
            }
            {Object.keys(carEntities).map(icao => (
                <SyncCarContainer
                    key={icao}
                    icao={icao}
                    entity={carEntities[icao]}
                />
            ))}
            {Object.keys(planeEntities).map(icao => (
                <SyncPlaneContainer
                    key={icao}
                    icao={icao}
                    entity={planeEntities[icao]}
                />
            ))}
        </React.Fragment>
    );
}

export default SyncTargets;